<template>
  <section id="data-entry-template-generation" class="page-wrapper" v-loading="loading || downloading"
    :element-loading-text="downloading ? 'Generating and preparing your ZIP download...' : 'Processing your spreadsheet...'"
    element-loading-background="rgba(255, 255, 255, 0.86)">
    <div class="page-header">
      <h2>Data Entry Template Generation</h2>
      <p>Upload a spreadsheet to generate facility templates. Rows without a Facility are excluded.</p>
    </div>
    <div class="template-type-panel">
      <label for="template-type">Template Type</label>

      <el-radio-group id="template-type" v-model="templateType" :disabled="loading || downloading"
        @change="handleTemplateTypeChange">
        <el-radio-button label="insurance">
          Insurance Exceptions
        </el-radio-button>

        <el-radio-button label="icd">
          ICD Exceptions
        </el-radio-button>
      </el-radio-group>
    </div>
    <div class="upload-panel" v-loading="loading">
      <div class="upload-heading">
        <h3>Spreadsheet Upload</h3>
        <span>.xlsx, .xls, or .csv; Details worksheet is used when available</span>
      </div>
      <div class="upload-controls">
        <el-upload ref="uploadRef" :auto-upload="false" :show-file-list="false" accept=".xlsx,.xls,.csv"
          :on-change="handleFileChange">
          <el-button plain icon="el-icon-folder-opened">Choose file</el-button>
        </el-upload>
        <div v-if="selectedFile" class="selected-file">
          <strong :title="selectedFile.name">{{ selectedFile.name }}</strong>
          <span>{{ formatFileSize(selectedFile.size) }}</span>
          <el-button type="text" icon="el-icon-close" title="Remove file" aria-label="Remove file" @click="clearFile" />
        </div>
        <el-button type="primary" :disabled="!selectedFile || loading" @click="processFile">
          {{ loading ? "Processing..." : "Upload & Process" }}
        </el-button>
      </div>
    </div>

    <div v-if="result" class="results-panel">
      <div class="results-heading">
        <div>
          <h3>Validation Summary</h3>
          <p>Upload ID: {{ result.uploadId }}</p>
        </div>
        <div class="summary">
          <span>Total Records <b>{{ result.totalRecords }}</b></span>
          <span>Valid Records <b>{{ result.validRecords }}</b></span>
          <span>Invalid Records <b>{{ result.invalidRecords.length }}</b></span>
        </div>
      </div>

      <div v-if="result.facilities.length" class="facility-list">
        <div class="facility-list-heading">
          <h3>Generated Facility Templates</h3>
          <el-button type="primary" icon="el-icon-download" :loading="downloading" :disabled="downloading"
            @click="downloadAllFacilities">
            {{ downloading ? "Preparing download..." : "Download All as ZIP" }}
          </el-button>
        </div>
        <p v-if="downloading" class="download-status" role="status">
          Generating the templates and preparing your download. This may take a moment.
        </p>
        <div v-for="facility in result.facilities" :key="facility.facilityName" class="facility-row">
          <div>
            <strong>{{ facility.facilityName }}</strong>
            <span>{{ facility.recordCount }} Records</span>
          </div>
        </div>
      </div>
      <p v-else class="no-records">No valid facility records found.</p>

      <el-alert v-if="result.invalidRecords.length"
        :title="`${result.invalidRecords.length} rows excluded: Facility is required.`" type="warning" show-icon
        :closable="false" />
    </div>
  </section>
</template>

<script>
import axios from "@/config/axios"
import UserPermissionsHelper from "@/mixins/UserPermissionsHelper"

const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000
const DOWNLOAD_TIMEOUT_MS = 30 * 60 * 1000

export default {
  name: "DataEntryTemplateGeneration",
  mixins: [UserPermissionsHelper],
  data() {
    return {
      loading: false,
      downloading: false,
      selectedFile: null,
      result: null,
      templateType: "insurance",

      templateEndpoints: {
        insurance: {
          upload: "/data-entry-template-generation/uploads",
          download: (uploadId) =>
            `/data-entry-template-generation/uploads/${uploadId}/download?templateType=insurance`,
        },
        icd: {
          upload: "/data-entry-template-generation/uploads",
          download: (uploadId) =>
            `/data-entry-template-generation/uploads/${uploadId}/download?templateType=icd`,
        },
      },
    }
  },
  methods: {
    handleTemplateTypeChange() {
      this.clearFile()
    },
    handleFileChange(file) {
      const extension = file.name.split(".").pop().toLowerCase()
      if (!["xlsx", "xls", "csv"].includes(extension)) {
        this.$message.error("Please upload an Excel or CSV file (.xlsx, .xls, or .csv)")
        return
      }
      this.selectedFile = file.raw
      this.result = null
    },
    clearFile() {
      this.result = null
      this.selectedFile = null
      if (this.$refs.uploadRef) {
        this.$refs.uploadRef.clearFiles()
      }
    },
    formatFileSize(bytes) {
      if (!bytes) return "0 Bytes"
      const units = ["Bytes", "KB", "MB", "GB"]
      const index = Math.floor(Math.log(bytes) / Math.log(1024))
      return `${parseFloat((bytes / Math.pow(1024, index)).toFixed(2))} ${units[index]}`
    },
    async getApiErrorMessage(error, fallbackMessage) {
      const responseData = error?.response?.data
      if (responseData?.text instanceof Function) {
        try {
          const parsedResponse = JSON.parse(await responseData.text())
          return parsedResponse.message || fallbackMessage
        } catch (parseError) {
          return fallbackMessage
        }
      }
      return responseData?.message || fallbackMessage
    },
    async processFile() {
      if (!this.selectedFile || this.loading) return
      this.loading = true
      try {
        const formData = new FormData()
        formData.append("file", this.selectedFile)
        const endpoint =
          this.templateEndpoints[this.templateType].upload
        const response = await axios.post(endpoint, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: UPLOAD_TIMEOUT_MS,
        })
        this.result = response.data.data
        this.$message.success(
          `${this.templateType === "icd" ? "ICD Exceptions" : "Insurance Exceptions"} processed successfully`
        )
      } catch (error) {
        this.$message.error(
          await this.getApiErrorMessage(
            error,
            "Unable to process the spreadsheet"
          )
        )
      } finally {
        this.loading = false
      }
    },
    async downloadAllFacilities() {
      if (this.downloading || !this.result) return
      this.downloading = true
      try {
        const endpoint = this.templateEndpoints[this.templateType].download(this.result.uploadId)
        const response = await axios.get(endpoint, {
          responseType: "blob",
          timeout: DOWNLOAD_TIMEOUT_MS,
        })
        const url = window.URL.createObjectURL(response.data)
        const link = document.createElement("a")
        link.href = url
        link.download =`${this.templateType}_exception_templates_${this.result.uploadId}.zip`
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.$message.error(
          await this.getApiErrorMessage(
            error,
            "Unable to download the templates ZIP"
          )
        )
      } finally {
        this.downloading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
#data-entry-template-generation {
  padding: 20px 32px;
  color: #263238;
  background: #f5f7f9;
  min-height: calc(100vh - 100px);

  .page-header {
    margin-bottom: 16px;

    h2 {
      margin: 0 0 4px;
      font-size: 24px;
    }

    p {
      margin: 0;
      color: #607d8b;
    }
  }

  .upload-panel,
  .results-panel {
    background: #fff;
    border: 1px solid #dfe5e9;
    padding: 16px 24px;
    margin-bottom: 16px;
  }

  h3 {
    margin: 0;
    font-size: 17px;
  }

  .upload-heading {
    display: flex;
    align-items: baseline;
    gap: 12px;
    margin-bottom: 12px;
    flex-wrap: wrap;
  }

  .upload-heading span {
    color: #78909c;
    font-size: 13px;
  }

  .upload-controls {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .selected-file {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    max-width: 100%;
    color: #455a64;
  }

  .selected-file strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .selected-file span,
  .facility-row span {
    color: #78909c;
    font-size: 13px;
    white-space: nowrap;
  }

  .results-heading {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    align-items: flex-start;
    margin-bottom: 12px;
  }

  .results-heading h3 {
    margin-bottom: 4px;
  }

  .results-heading p {
    margin: 0;
    color: #78909c;
    font-size: 13px;
    overflow-wrap: anywhere;
  }

  .summary {
    display: flex;
    gap: 18px;
    flex-wrap: wrap;
    color: #607d8b;
    font-size: 13px;
  }

  .summary span {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .summary b {
    color: #263238;
    font-size: 18px;
  }

  .facility-list-heading {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    border-bottom: 1px solid #eceff1;
    padding-bottom: 8px;
  }

  .download-status {
    margin: 10px 0 0;
    color: #607d8b;
    font-size: 13px;
  }

  .facility-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding: 10px 0;
    border-bottom: 1px solid #eceff1;
  }

  .facility-row div {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .no-records {
    margin: 0;
    color: #78909c;
  }

  @media (max-width: 700px) {
    padding: 16px;

    .results-heading {
      flex-direction: column;
    }
    .selected-file {
      width: 100%;
    }
  }
  .template-type-panel {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    padding: 16px 24px;
    margin-bottom: 16px;
    background: #fff;
    border: 1px solid #dfe5e9;
    label {
      color: #263238;
      font-weight: 500;
    }
  }
}
</style>